/**
 * PURPOSE: Administrar las instituciones participantes desde una sola vista.
 * RESPONSIBILITIES: Mostrar participantes actuales, agregar mediante búsqueda y eliminar de forma protegida.
 * COLLABORATORS: FasesArea.jsx, react-select y phaseToolsService.js.
 * ROUTE: winscore/src/area/FasesArea/components/ParticipantsManagerModal.jsx
 */
import React, { useEffect, useMemo, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import Select from "react-select";
import { FiTrash2, FiUserPlus, FiUsers } from "react-icons/fi";
import { toast } from "react-hot-toast";
import {
  addPhaseParticipants,
  deletePhaseParticipant,
} from "../../../services/phaseToolsService.js";

const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const InstitutionLogo = ({ institution, size = "h-11 w-11" }) => (
  <span
    className={`relative flex ${size} shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-100 text-xs font-black text-slate-500`}
  >
    {(institution?.abrev || institution?.name || "--")
      .slice(0, 2)
      .toUpperCase()}
    {institution?.logoUrl ? (
      <img
        src={institution.logoUrl}
        alt=""
        className="absolute inset-0 h-full w-full bg-white object-contain p-1"
        onError={(event) => {
          event.currentTarget.style.display = "none";
        }}
      />
    ) : null}
  </span>
);

const groupLabel = (participant) => {
  if (!participant.idgrupo) return "Sin grupo";
  if (participant.group?.name) return participant.group.name;
  return `Grupo ${participant.group?.nrogrupo || participant.idgrupo}`;
};

const participantName = (participant) =>
  participant?.institution?.abrev ||
  participant?.institution?.name ||
  `Participante ${participant?.idparticipant}`;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 48,
    borderRadius: 12,
    borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 2px #dbeafe" : "none",
    "&:hover": { borderColor: "#60a5fa" },
  }),
  multiValue: (base) => ({
    ...base,
    borderRadius: 8,
    backgroundColor: "#eff6ff",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#1d4ed8",
    fontWeight: 800,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 1800 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#eff6ff" : "#ffffff",
    color: "#0f172a",
    cursor: "pointer",
  }),
};

const ParticipantsManagerModal = ({
  open,
  onClose,
  idevent,
  idsport,
  context,
  onChanged,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const participants = useMemo(() => {
    const rows = [...(context?.participants || [])];
    return rows.sort(
      (a, b) =>
        Number(a.idgrupo || Number.MAX_SAFE_INTEGER) -
          Number(b.idgrupo || Number.MAX_SAFE_INTEGER) ||
        Number(a.nro_grupo || Number.MAX_SAFE_INTEGER) -
          Number(b.nro_grupo || Number.MAX_SAFE_INTEGER) ||
        participantName(a).localeCompare(participantName(b), "es", {
          sensitivity: "base",
        })
    );
  }, [context?.participants]);
  const pendingOptions = useMemo(
    () =>
      (context?.institutions || [])
        .filter((institution) => !institution.added)
        .map((institution) => ({
          value: Number(institution.idinstitution),
          label: institution.abrev || institution.name,
          institution,
          search: normalize(
            `${institution.name} ${institution.abrev || ""} ${
              institution.idinstitution
            }`
          ),
        })),
    [context?.institutions]
  );
  const selectedOptions = useMemo(
    () =>
      pendingOptions.filter((option) => selectedIds.includes(option.value)),
    [pendingOptions, selectedIds]
  );
  const busy = saving || deleting;

  useEffect(() => {
    if (open) {
      setSelectedIds([]);
      setDeleteTarget(null);
    }
  }, [open]);

  useEffect(() => {
    setSelectedIds((current) =>
      current.filter((id) =>
        pendingOptions.some((option) => option.value === id)
      )
    );
  }, [pendingOptions]);

  const handleSave = async () => {
    if (!selectedIds.length) {
      toast.error("Selecciona al menos una institución.");
      return;
    }
    try {
      setSaving(true);
      const result = await addPhaseParticipants({
        idevent,
        idsport,
        institutionIds: selectedIds,
      });
      toast.success(`${result.created} institución(es) agregada(s).`);
      setSelectedIds([]);
      await onChanged?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "No se pudieron agregar las instituciones."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await deletePhaseParticipant({
        idevent,
        idsport,
        idparticipant: deleteTarget.idparticipant,
      });
      toast.success(`${participantName(deleteTarget)} fue eliminado.`);
      setDeleteTarget(null);
      await onChanged?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "No se pudo eliminar al participante."
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={busy ? undefined : onClose} sx={{ p: 2 }}>
        <Sheet className="mx-auto flex max-h-[92vh] w-[min(96vw,920px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
          <div className="relative border-b border-slate-200 px-5 py-4 pr-14">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-600">
              Fase de grupos
            </p>
            <h2 className="mt-1 flex items-center gap-2 text-xl font-black text-slate-900">
              <FiUsers className="text-blue-600" /> Instituciones participantes
            </h2>
            <ModalClose disabled={busy} />
          </div>

          <div className="flex-1 overflow-y-auto p-5">
            <section>
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-sm font-black text-slate-800">
                  Participantes actuales
                </h3>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-600">
                  {participants.length}
                </span>
              </div>

              {participants.length ? (
                <div className="grid gap-2 md:grid-cols-2">
                  {participants.map((participant) => {
                    const institution = participant.institution || {};
                    return (
                      <div
                        key={participant.idparticipant}
                        className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3"
                      >
                        <InstitutionLogo institution={institution} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-black text-slate-900">
                            {participantName(participant)}
                          </p>
                          <p className="truncate text-xs font-semibold text-slate-500">
                            {institution.name || "Institución"}
                          </p>
                          <p className="mt-1 text-[11px] font-bold text-blue-700">
                            {groupLabel(participant)}
                            {participant.nro_grupo
                              ? ` · Posición ${participant.nro_grupo}`
                              : ""}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(participant)}
                          disabled={busy || context?.config?.isClosed}
                          title={`Eliminar a ${participantName(participant)}`}
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm font-semibold text-slate-500">
                  Aún no hay participantes agregados.
                </div>
              )}
            </section>

            <section className="mt-6 border-t border-slate-200 pt-5">
              <div className="mb-3 flex items-center gap-2">
                <FiUserPlus className="text-blue-600" />
                <h3 className="text-sm font-black text-slate-800">
                  Agregar instituciones
                </h3>
              </div>
              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                <Select
                  value={selectedOptions}
                  onChange={(options) =>
                    setSelectedIds((options || []).map((option) => option.value))
                  }
                  options={pendingOptions}
                  isMulti
                  isSearchable
                  isDisabled={busy || context?.config?.isClosed}
                  closeMenuOnSelect={false}
                  hideSelectedOptions
                  placeholder="Buscar institución por nombre, abreviatura o ID..."
                  noOptionsMessage={() => "No hay instituciones pendientes"}
                  filterOption={(candidate, inputValue) =>
                    candidate.data.search.includes(normalize(inputValue))
                  }
                  formatOptionLabel={(option, meta) =>
                    meta.context === "value" ? (
                      <span>{option.label}</span>
                    ) : (
                      <span className="flex items-center gap-3">
                        <InstitutionLogo
                          institution={option.institution}
                          size="h-9 w-9"
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-black text-slate-800">
                            {option.label}
                          </span>
                          <span className="block truncate text-xs text-slate-500">
                            {option.institution.name} · ID {option.value}
                          </span>
                        </span>
                      </span>
                    )
                  }
                  styles={selectStyles}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : undefined
                  }
                  menuPosition="fixed"
                  menuPlacement="auto"
                />
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={busy || !selectedIds.length}
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 font-black text-white shadow-lg shadow-blue-500/20 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FiUserPlus />
                  {saving ? "Agregando..." : "Agregar seleccionadas"}
                </button>
              </div>
            </section>
          </div>

          <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-xl border border-slate-300 bg-white px-5 py-2.5 font-bold text-slate-700 disabled:opacity-50"
            >
              Cerrar
            </button>
          </div>
        </Sheet>
      </Modal>

      <Modal
        open={Boolean(deleteTarget)}
        onClose={deleting ? undefined : () => setDeleteTarget(null)}
        sx={{ p: 2, zIndex: 1900 }}
      >
        <Sheet className="mx-auto w-[min(92vw,440px)] rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
          <h3 className="pr-8 text-lg font-black text-slate-900">
            ¿Eliminar a {participantName(deleteTarget)} de este deporte?
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Esta acción solo continuará si el participante no tiene actividad
            competitiva registrada.
          </p>
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
              className="rounded-xl border border-slate-300 px-5 py-2.5 font-bold text-slate-700 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 font-black text-white hover:bg-red-700 disabled:opacity-50"
            >
              <FiTrash2 /> {deleting ? "Eliminando..." : "Eliminar"}
            </button>
          </div>
        </Sheet>
      </Modal>
    </>
  );
};

export default ParticipantsManagerModal;
